import { Badge, Button } from '@devizy/ui';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/settings/promo')({
  component: PromoPage,
});

function MyPromoCodes(): React.ReactElement | null {
  const { data, isLoading } = trpc.promo.myCodes.useQuery();
  if (isLoading || !data || data.length === 0) return null;
  return (
    <section className="border-border bg-card rounded-2xl border p-6 shadow-[0_8px_24px_-12px_hsl(30_10%_30%/0.06)]">
      <h3 className="text-[13px] font-[660] tracking-[-0.01em]">
        Codes qui vous ont été attribués
      </h3>
      <p className="text-muted-foreground-soft mt-0.5 text-[11.5px]">
        Saisissez-les au moment du règlement de la commission.
      </p>
      <ul className="mt-4 space-y-2.5">
        {data.map((entry) => (
          <li
            key={entry.id}
            className="border-border-soft flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3.5"
            data-testid={`my-promo-${entry.code}`}
          >
            <div className="min-w-0">
              <span className="font-mono text-sm font-[650] tracking-[0.06em] tabular-nums">
                {entry.code}
              </span>
              {entry.label ? (
                <p className="text-muted-foreground truncate text-[11.5px]">{entry.label}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="success">
                {entry.discountType === 'PERCENTAGE'
                  ? `-${String(entry.discountValue)} %`
                  : `-${String(entry.discountValue)} ${entry.referenceCurrency ?? 'EUR'}`}
              </Badge>
              <span className="text-muted-foreground text-[11.5px]">
                {entry.expiresAt
                  ? `jusqu'au ${new Date(entry.expiresAt).toLocaleDateString('fr-FR')}`
                  : 'sans échéance'}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PromoPage(): React.ReactElement {
  const [code, setCode] = React.useState('');
  const [submitted, setSubmitted] = React.useState('');

  const { data, isFetching, refetch } = trpc.promo.validate.useQuery(
    { code: submitted, baseAmount: 0, currency: 'EUR' },
    { enabled: submitted.length > 0 },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setSubmitted(trimmed);
    void refetch();
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="font-display text-base tracking-tight">Codes promo</h2>
        <p className="text-muted-foreground mt-1 text-xs">
          Vérifiez la validité d&apos;un code promotionnel.
        </p>
      </div>

      <MyPromoCodes />

      <section className="border-border bg-card rounded-2xl border p-6 shadow-[0_8px_24px_-12px_hsl(30_10%_30%/0.06)]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex gap-2 sm:max-w-md">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ex. WELCOME20"
              className="border-input bg-background focus-visible:ring-ring h-10 flex-1 rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none"
            />
            <Button type="submit" variant="primary" size="md" disabled={isFetching || !code.trim()}>
              {isFetching ? 'Vérification…' : 'Vérifier'}
            </Button>
          </div>
        </form>
      </section>

      {data && submitted && (
        <div
          className={`rounded-2xl border p-5 ${data.valid ? 'border-success/30 bg-success/5' : 'border-destructive/30 bg-destructive/5'}`}
        >
          {data.valid ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="success">Valide</Badge>
                <span className="font-mono text-sm font-medium tabular-nums">{submitted}</span>
              </div>
              <p className="text-muted-foreground text-sm">
                Réduction&nbsp;:{' '}
                {data.discountType === 'PERCENTAGE'
                  ? `${data.discountValue}%`
                  : `${data.discountValue} €`}
                {data.applicationDurationMonths
                  ? ` pendant ${data.applicationDurationMonths} mois`
                  : ''}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <Badge variant="destructive">Invalide</Badge>
              <p className="text-muted-foreground text-sm">
                {data.reason === 'NOT_FOUND' && 'Code introuvable.'}
                {data.reason === 'NOT_ELIGIBLE' && "Vous n'êtes pas éligible à ce code."}
                {data.reason === 'MAX_GLOBAL_USES_REACHED' &&
                  "Ce code a atteint sa limite d'utilisation."}
                {data.reason === 'MAX_USES_PER_USER_REACHED' && 'Vous avez déjà utilisé ce code.'}
                {data.reason === 'BELOW_MIN_AMOUNT' && 'Montant minimum non atteint.'}
                {data.reason === 'CURRENCY_MISMATCH' &&
                  "Ce code n'est pas valide pour votre devise."}
                {![
                  'NOT_FOUND',
                  'NOT_ELIGIBLE',
                  'MAX_GLOBAL_USES_REACHED',
                  'MAX_USES_PER_USER_REACHED',
                  'BELOW_MIN_AMOUNT',
                  'CURRENCY_MISMATCH',
                ].includes(data.reason) && 'Code invalide.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
